import { useMemo, useState } from 'react'
import { Button, Group, Modal, Select, Skeleton, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconKey, IconPlugConnectedX } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Callout, DataCard, EmptyState } from '@/components/ui'
import { useCanNaPlataforma } from '@/store/auth'
import {
  deleteConnection, listApplications, listClientes, listConnections, provisionApplication,
  setApplicationStatus, setConnectionStatus,
  type SsoAppProvisionResult, type SsoApplication, type SsoConnection,
} from '@/api/sso'
import { ssoErrorMessage } from './ssoErrors'
import { ConexaoDrawer } from './ConexaoDrawer'
import { AplicacaoDrawer } from './AplicacaoDrawer'
import { ProvedorEntraCard } from './ProvedorEntraCard'
import { AplicacoesTable } from './AplicacoesTable'

// Tela de PLATAFORMA (TASK-24956): um provedor só — o Entra da TecToy, que o backend acha
// no tenant do provedor — e as aplicações de todos os clientes. Não depende do seletor de
// tenant; ver e editar exigem admin:platform.
export function ConexoesSso() {
  const can = useCanNaPlataforma()
  const podeGerenciar = can('admin', 'platform')
  const qc = useQueryClient()

  const [cliente, setCliente] = useState<string | null>(null)
  const [conexaoDrawer, setConexaoDrawer] = useState<{ aberto: boolean; conexao: SsoConnection | null }>({ aberto: false, conexao: null })
  const [appDrawer, setAppDrawer] = useState<{ aberto: boolean; app: SsoApplication | null }>({ aberto: false, app: null })
  const [excluindo, setExcluindo] = useState(false)
  const [confirmacao, setConfirmacao] = useState('')

  const conexoesQ = useQuery({ queryKey: ['sso-conexoes'], queryFn: () => listConnections() })
  const conexao = (conexoesQ.data ?? []).find((c) => c.is_platform_provider) ?? null
  const realm = conexao?.keycloak_realm ?? null

  const appsQ = useQuery({ queryKey: ['sso-apps'], queryFn: () => listApplications(), enabled: conexao != null })
  const apps = useMemo(() => appsQ.data ?? [], [appsQ.data])
  // O filtro só oferece cliente que tem aplicação: filtrar por quem não tem é tabela vazia.
  const clientesComApp = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of apps) m.set(String(a.tenant_id), a.tenant_name ?? `tenant ${a.tenant_id}`)
    return [...m].map(([value, label]) => ({ value, label })).sort((x, y) => x.label.localeCompare(y.label, 'pt-BR'))
  }, [apps])
  const filtradas = cliente ? apps.filter((a) => String(a.tenant_id) === cliente) : apps
  // A exclusão do provedor é recusada pelo backend com app ativa no realm, de qualquer cliente.
  const temAppAtiva = apps.some((a) => a.status === 'active' && a.keycloak_realm === realm)

  const clientesQ = useQuery({ queryKey: ['sso-clientes'], queryFn: () => listClientes(), enabled: podeGerenciar })
  const opcoesCliente = useMemo(
    () => (clientesQ.data ?? []).filter((t) => t.is_active == null || Boolean(t.is_active)).map((t) => ({
      value: String(t.id), label: t.environment ? `${t.name} · ${t.environment}` : t.name,
    })),
    [clientesQ.data],
  )

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['sso-conexoes'] })
    void qc.invalidateQueries({ queryKey: ['sso-apps'] })
  }
  const aoErrar = (err: unknown) =>
    notifications.show({ color: 'red', title: 'Não foi possível concluir', message: ssoErrorMessage(err).message })
  const avisar = (msg: string, lastError?: string | null) =>
    lastError
      ? notifications.show({ color: 'red', title: 'Provisionamento falhou', message: `Provisionamento falhou: ${lastError}` })
      : notifications.show({ color: 'green', message: msg })

  const statusConexao = useMutation({
    mutationFn: (v: { status: 'testing' | 'paused' | 'active'; msg: string }) =>
      setConnectionStatus((conexao as SsoConnection).id, v.status),
    onSuccess: (c, v) => { invalidar(); avisar(v.msg, c.config?.last_error) },
    onError: aoErrar,
  })
  const excluirConexao = useMutation({
    mutationFn: () => deleteConnection((conexao as SsoConnection).id),
    onSuccess: () => { invalidar(); setExcluindo(false); setConfirmacao(''); avisar('Provedor excluído') },
    onError: aoErrar,
  })
  const statusApp = useMutation({
    mutationFn: (v: { app: SsoApplication; status: 'paused' | 'active' }) => setApplicationStatus(v.app.id, v.status),
    onSuccess: (_r, v) => { invalidar(); avisar(v.status === 'paused' ? 'Aplicação pausada' : 'Aplicação reativada') },
    onError: aoErrar,
  })
  // Rascunho: provisiona a linha existente, nunca cria outra.
  const provisionar = useMutation({
    mutationFn: (a: SsoApplication) => provisionApplication(a.id),
    onSuccess: (r) => { invalidar(); avisar('Aplicação provisionada', r.last_error) },
    onError: aoErrar,
  })

  // Só a PRIMEIRA carga sem dado vira tela de erro. Um refetch que falha com a lista já
  // carregada (o GET abortado logo depois do Reprovisionar, 499 no nginx em 06/10) mantém
  // a tela e avisa embaixo do cartão (TASK-24685).
  if (conexoesQ.isError && conexoesQ.data == null) {
    const semGateway = (conexoesQ.error as { response?: { status?: number; data?: { detail?: string } } })
      ?.response?.data?.detail === 'sso_disabled'
    return (
      <EmptyState
        icon={IconPlugConnectedX}
        title={semGateway ? 'Gateway SSO desligado neste ambiente' : 'Não foi possível carregar o provedor'}
        description={semGateway ? 'O gateway de SSO não está habilitado aqui.' : ssoErrorMessage(conexoesQ.error).message}
        secondaryAction={semGateway ? undefined : { label: 'Tentar de novo', onClick: () => void conexoesQ.refetch() }}
      />
    )
  }
  if (conexoesQ.isLoading) return <Skeleton height={180} />

  const fecharConexao = () => setConexaoDrawer({ aberto: false, conexao: null })

  if (conexao == null || realm == null) {
    return (
      <>
        <EmptyState
          icon={IconKey}
          title="Nenhum provedor de identidade"
          description="Conecte o Entra ID da plataforma para publicar as aplicações dos clientes."
          primaryAction={podeGerenciar ? { label: 'Configurar Entra ID', onClick: () => setConexaoDrawer({ aberto: true, conexao: null }) } : undefined}
        />
        <ConexaoDrawer
          opened={conexaoDrawer.aberto} conexao={conexaoDrawer.conexao}
          onClose={fecharConexao} onSaved={invalidar}
        />
      </>
    )
  }

  return (
    <Stack gap="md">
      {conexoesQ.isError && (
        <Callout variant="warning" title="Não foi possível atualizar a lista">
          <Group gap="sm" justify="flex-start">
            <Text size="sm">{ssoErrorMessage(conexoesQ.error).message}</Text>
            <Button size="xs" variant="default" onClick={() => void conexoesQ.refetch()}>Tentar de novo</Button>
          </Group>
        </Callout>
      )}
      <ProvedorEntraCard
        conexao={conexao} podeGerenciar={podeGerenciar} temAppAtiva={temAppAtiva}
        onTrocar={() => setConexaoDrawer({ aberto: true, conexao })}
        onReprovisionar={() => statusConexao.mutate({ status: 'testing', msg: 'Provedor provisionado' })}
        onPausarOuReativar={() => statusConexao.mutate(
          conexao.status === 'active'
            ? { status: 'paused', msg: 'Provedor pausado' }
            : { status: 'active', msg: 'Provedor reativado' })}
        onExcluir={() => setExcluindo(true)}
      />
      {appsQ.isError && <Callout variant="danger">{ssoErrorMessage(appsQ.error).message}</Callout>}
      <DataCard title="Aplicações de todos os clientes" loading={appsQ.isLoading}>
        <Select
          label="Cliente" placeholder="Todos os clientes" maw={320} mb="sm"
          data={clientesComApp} value={cliente} onChange={setCliente} clearable searchable
        />
        <AplicacoesTable
          apps={filtradas} provedorAtivo={conexao.status === 'active'} podeGerenciar={podeGerenciar}
          onNova={() => setAppDrawer({ aberto: true, app: null })}
          onEditar={(app) => setAppDrawer({ aberto: true, app })}
          onProvisionar={(a) => provisionar.mutate(a)}
          onStatus={(app, status) => statusApp.mutate({ app, status })}
        />
      </DataCard>

      <ConexaoDrawer
        opened={conexaoDrawer.aberto} conexao={conexaoDrawer.conexao}
        onClose={fecharConexao} onSaved={invalidar}
      />
      <AplicacaoDrawer
        opened={appDrawer.aberto} clientes={opcoesCliente} app={appDrawer.app}
        onClose={() => setAppDrawer({ aberto: false, app: null })}
        onChanged={invalidar}
        onSaved={(r: SsoAppProvisionResult | SsoApplication) => {
          invalidar()
          if ('config' in r) avisar('Aplicação atualizada', r.config?.last_error)
          else avisar('Aplicação provisionada', r.last_error)
        }}
      />
      <Modal opened={excluindo} onClose={() => { setExcluindo(false); setConfirmacao('') }} title="Excluir provedor">
        <Stack gap="sm">
          <Text size="sm">Isto remove o provedor Entra do realm. Digite o nome do realm para confirmar.</Text>
          <TextInput label="Digite o nome do realm" value={confirmacao} onChange={(e) => setConfirmacao(e.currentTarget.value)} />
          <Group justify="flex-start">
            <Button color="red" disabled={confirmacao !== realm} loading={excluirConexao.isPending}
              onClick={() => excluirConexao.mutate()}>
              Excluir provedor
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
